import { useState, useCallback } from "react";

export function useCollectionGames() {
  // Tab management
  const [activeTab, setActiveTab] = useState('my-collection');
  const [tabData, setTabData] = useState({
    'my-collection': {
      collection: [],
      loading: false,
      sortBy: "year_published",
      sortOrder: "desc",
      collectionSearchQuery: "",
      filterMode: "all",
      displayedItems: [],
      hasMore: true,
      isLoadingMore: false,
      lastPlayedData: {}
    }
  });

  // Initialize tab data when first accessed
  const initializeTabData = useCallback((tabId) => {
    setTabData(prev => {
      if (prev[tabId]) return prev;
      return {
        ...prev,
        [tabId]: {
          collection: [],
          loading: false,
          sortBy: "year_published",
          sortOrder: "desc",
          collectionSearchQuery: "",
          filterMode: "all",
          displayedItems: [],
          hasMore: true,
          isLoadingMore: false,
          lastPlayedData: {}
        }
      };
    });
  }, []);

  // Get current tab data
  const currentTabData = tabData[activeTab] || tabData['my-collection'];

  // Update current tab data
  const updateCurrentTabData = useCallback((updates) => {
    setTabData(prev => ({
      ...prev,
      [activeTab]: {
        ...prev[activeTab],
        ...updates
      }
    }));
  }, [activeTab]);

  // Legacy accessors for backward compatibility (stabilized with useCallback)
  const setCollection = useCallback((newCollection) => {
    if (typeof newCollection === 'function') {
      setTabData(prev => {
        const current = prev[activeTab] || prev['my-collection'];
        return {
          ...prev,
          [activeTab]: {
            ...current,
            collection: newCollection(current.collection)
          }
        };
      });
    } else {
      updateCurrentTabData({ collection: newCollection });
    }
  }, [activeTab, updateCurrentTabData]);

  const setLoading = useCallback((newLoading) =>
    updateCurrentTabData({ loading: newLoading }),
  [updateCurrentTabData]
  );
  const setSortBy = useCallback((newSortBy) =>
    updateCurrentTabData({ sortBy: newSortBy }),
  [updateCurrentTabData]
  );
  const setSortOrder = useCallback((newSortOrder) =>
    updateCurrentTabData({ sortOrder: newSortOrder }),
  [updateCurrentTabData]
  );
  const setCollectionSearchQuery = useCallback((newQuery) =>
    updateCurrentTabData({ collectionSearchQuery: newQuery }),
  [updateCurrentTabData]
  );
  const setFilterMode = useCallback((newMode) =>
    updateCurrentTabData({ filterMode: newMode }),
  [updateCurrentTabData]
  );
  const setDisplayedItems = useCallback((newItems) => {
    if (typeof newItems === 'function') {
      setTabData(prev => {
        const current = prev[activeTab] || prev['my-collection'];
        return {
          ...prev,
          [activeTab]: {
            ...current,
            displayedItems: newItems(current.displayedItems)
          }
        };
      });
    } else {
      updateCurrentTabData({ displayedItems: newItems });
    }
  }, [activeTab, updateCurrentTabData]);

  const setHasMore = useCallback((newHasMore) =>
    updateCurrentTabData({ hasMore: newHasMore }),
  [updateCurrentTabData]
  );
  const setIsLoadingMore = useCallback((newLoading) =>
    updateCurrentTabData({ isLoadingMore: newLoading }),
  [updateCurrentTabData]
  );
  const setLastPlayedData = useCallback((newData) =>
    updateCurrentTabData({ lastPlayedData: newData }),
  [updateCurrentTabData]
  );

  // Direct accessors for reading values (no stabilization needed)
  const collection = currentTabData.collection;
  const loading = currentTabData.loading;
  const sortBy = currentTabData.sortBy;
  const sortOrder = currentTabData.sortOrder;
  const collectionSearchQuery = currentTabData.collectionSearchQuery;
  const filterMode = currentTabData.filterMode;
  const displayedItems = currentTabData.displayedItems;
  const hasMore = currentTabData.hasMore;
  const isLoadingMore = currentTabData.isLoadingMore;
  const lastPlayedData = currentTabData.lastPlayedData;

  const itemsPerLoad = 48;

  const resetCollectionState = useCallback(() => {
    setCollection([]);
    setDisplayedItems([]);
    setHasMore(true);
    setCollectionSearchQuery("");
    setFilterMode("all");
  }, [setCollection, setDisplayedItems, setHasMore, setCollectionSearchQuery, setFilterMode]);

  const toggleSortOrder = useCallback(() => {
    setSortOrder(currentTabData.sortOrder === "desc" ? "asc" : "desc");
  }, [setSortOrder, currentTabData.sortOrder]);

  return {
    // Tab management
    activeTab,
    setActiveTab,
    tabData,
    initializeTabData,
    currentTabData,
    updateCurrentTabData,
    // Legacy collection data (backward compatibility)
    collection,
    setCollection,
    loading,
    setLoading,
    // Sorting
    sortBy,
    setSortBy,
    sortOrder,
    setSortOrder,
    toggleSortOrder,
    // Filtering
    collectionSearchQuery,
    setCollectionSearchQuery,
    filterMode,
    setFilterMode,
    // Pagination
    displayedItems,
    setDisplayedItems,
    itemsPerLoad,
    hasMore,
    setHasMore,
    isLoadingMore,
    setIsLoadingMore,
    // Last played
    lastPlayedData,
    setLastPlayedData,
    resetCollectionState
  };
}
